import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, BellSimple, CheckCircle, CalendarCheck, CreditCard, ArrowClockwise, Check } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Spinner } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";

const ICONS = { booking: CalendarCheck, payment: CreditCard, refund: ArrowClockwise, admin: BellSimple, general: BellSimple };

export default function Notifications() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState(null);

  const load = () => api.get("/notifications").then((r) => setNotes(r.data));
  useEffect(() => { load(); }, []);

  const markAll = async () => { await api.patch("/notifications/read"); load(); };
  const markOne = async (id) => { await api.patch(`/notifications/${id}/read`); load(); };

  const unread = (notes || []).filter((n) => !n.read).length;

  return (
    <div className="mx-auto min-h-screen max-w-lg bg-background md:max-w-2xl">
      <div className="sticky top-0 z-30 flex items-center justify-between border-b border-stone-200 bg-white/90 px-4 py-4 backdrop-blur-xl">
        <button data-testid="notif-back" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-stone-600"><ArrowLeft size={18} /> Back</button>
        <h1 className="font-heading text-lg font-semibold">Notifications {unread > 0 && <span className="ml-1 rounded-full bg-primary px-2 py-0.5 text-xs text-white">{unread}</span>}</h1>
        <button data-testid="notif-mark-all" onClick={markAll} className="text-sm font-medium text-primary disabled:text-stone-300" disabled={unread === 0}>Mark all read</button>
      </div>

      <div className="px-4 py-4">
        {notes === null ? (
          <div className="flex justify-center py-16"><Spinner /></div>
        ) : notes.length === 0 ? (
          <EmptyState icon={BellSimple} title="No notifications" message="You're all caught up. Booking and payment updates will appear here." />
        ) : (
          <div className="space-y-2">
            {notes.map((n) => {
              const Icon = ICONS[n.type] || BellSimple;
              return (
                <div key={n.id} data-testid={`notif-${n.id}`} className={`flex items-start gap-3 rounded-2xl border p-4 ${n.read ? "border-stone-200 bg-white" : "border-primary/20 bg-accent/40"}`}>
                  <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${n.read ? "bg-stone-100 text-stone-500" : "bg-primary/10 text-primary"}`}><Icon size={18} weight="duotone" /></span>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-stone-800">{n.title}</p>
                    <p className="text-sm text-stone-500">{n.message}</p>
                    <p className="mt-1 text-xs text-stone-400">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                  {!n.read && (
                    <Button data-testid={`notif-read-${n.id}`} size="icon" variant="ghost" className="h-8 w-8 rounded-full text-primary" onClick={() => markOne(n.id)}><Check size={16} /></Button>
                  )}
                  {n.read && <CheckCircle size={16} className="mt-1 text-stone-300" />}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
